/**
 * ProAmTaxiPortal — /proamtaxi
 * Taxi driver code verification & redemption dashboard.
 *
 * This is a React component for your existing SPA (same stack as RelayForLife.jsx).
 * Add the route in your router: <Route path="/proamtaxi" element={<ProAmTaxiPortal />} />
 *
 * WHAT THIS PAGE DOES:
 *  - Taxi driver enters a 10-char discount code
 *  - Page verifies it against Supabase in real-time
 *  - Shows full code + passenger details before confirming
 *  - Marks the code as 'used' and logs the taxi partner + timestamp
 *  - Displays the driver's own redemption history
 *  - Admin view shows all redemptions across all drivers
 *
 * REQUIRED: A taxi_partners row for each driver (you manage this via Supabase Studio).
 * Drivers log in using their normal ProAm account — their profile_id must match a
 * taxi_partners row (add a profile_id column to taxi_partners, see schema below).
 *
 * UPDATED taxi_partners schema addition:
 *   alter table taxi_partners add column profile_id uuid references profiles(id) unique;
 *   -- This links the driver's login to their taxi partner record.
 *
 * The "use code" action calls a Postgres RPC that atomically:
 *   1. Validates the code (exists, status='issued', not expired)
 *   2. Checks it hasn't been used by this taxi before (optional: any taxi)
 *   3. Updates status to 'used', sets used_at, taxi_partner_id
 *   4. Inserts audit row with taxi actor details
 *   5. Returns the updated redemption + passenger profile
 *
 * SQL for use_taxi_code RPC:
 * ---
 * create or replace function use_taxi_code(p_code text, p_taxi_partner_id uuid)
 * returns json language plpgsql security definer as $$
 * declare
 *   v_red  taxi_redemptions%rowtype;
 *   v_prof profiles%rowtype;
 * begin
 *   -- Lock + validate code
 *   select * into v_red from taxi_redemptions
 *     where code = upper(p_code) for update;
 *
 *   if not found then raise exception 'CODE_NOT_FOUND'; end if;
 *   if v_red.status <> 'issued' then raise exception 'CODE_ALREADY_USED'; end if;
 *   if v_red.expires_at < now() then
 *     update taxi_redemptions set status='expired' where id=v_red.id;
 *     raise exception 'CODE_EXPIRED';
 *   end if;
 *
 *   -- Mark used
 *   update taxi_redemptions set
 *     status           = 'used',
 *     used_at          = now(),
 *     taxi_partner_id  = p_taxi_partner_id
 *   where id = v_red.id
 *   returning * into v_red;
 *
 *   -- Fetch passenger profile
 *   select * into v_prof from profiles where id = v_red.profile_id;
 *
 *   -- Audit
 *   insert into taxi_redemption_audit (redemption_id, action, actor_id, actor_role, metadata)
 *   values (v_red.id, 'used', auth.uid(), 'taxi',
 *     jsonb_build_object('taxi_partner_id', p_taxi_partner_id, 'used_at', now()));
 *
 *   return json_build_object(
 *     'redemption', row_to_json(v_red),
 *     'passenger',  json_build_object(
 *       'name',    v_prof.name,
 *       'surname', v_prof.surname,
 *       'username',v_prof.username
 *     )
 *   );
 * end; $$;
 * ---
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../supabaseClient";
import logo from "../assets/logo.png";

/* ════════════════════════════════════════════════
   THEME (same as existing pages)
════════════════════════════════════════════════ */
const DARK = {
  mode: "dark",
  accent: "#0C69C8", accentGrad: "linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%)",
  accentBg: "rgba(12,105,200,0.10)", accentBorder: "rgba(12,105,200,0.20)",
  gold: "#F59E0B", goldLight: "rgba(245,158,11,0.12)", goldBorder: "rgba(245,158,11,0.30)",
  success: "#34D399", successBg: "rgba(52,211,153,0.08)", successBorder: "rgba(52,211,153,0.20)",
  danger: "#F87171", dangerBg: "rgba(248,113,113,0.10)", dangerBorder: "rgba(248,113,113,0.25)",
  amber: "#FBBF24", amberBg: "rgba(251,191,36,0.08)", amberBorder: "rgba(251,191,36,0.25)",
  bg: "#0A0A0A", bgCard: "#141414", bgAlt: "#111111", bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.08)", borderMedium: "rgba(255,255,255,0.12)",
  shadowMd: "0 4px 16px rgba(0,0,0,0.4)", shadowLg: "0 12px 48px rgba(0,0,0,0.6)",
  text: "#FAFAFA", textSecondary: "rgba(255,255,255,0.70)", textMuted: "rgba(255,255,255,0.40)",
  headerBg: "rgba(10,10,10,0.92)", scrollbarThumb: "rgba(255,255,255,0.12)",
  inputBg: "#1E1E1E",
};

const LIGHT = {
  mode: "light",
  accent: "#0C69C8", accentGrad: "linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%)",
  accentBg: "rgba(12,105,200,0.06)", accentBorder: "rgba(12,105,200,0.15)",
  gold: "#D97706", goldLight: "rgba(217,119,6,0.08)", goldBorder: "rgba(217,119,6,0.25)",
  success: "#059669", successBg: "rgba(5,150,105,0.07)", successBorder: "rgba(5,150,105,0.20)",
  danger: "#DC2626", dangerBg: "rgba(220,38,38,0.06)", dangerBorder: "rgba(220,38,38,0.20)",
  amber: "#D97706", amberBg: "rgba(217,119,6,0.07)", amberBorder: "rgba(217,119,6,0.25)",
  bg: "#F5F7FA", bgCard: "#FFFFFF", bgAlt: "#EDF0F5", bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)", borderMedium: "rgba(10,29,68,0.12)",
  shadowMd: "0 4px 16px rgba(10,29,68,0.08)", shadowLg: "0 12px 40px rgba(10,29,68,0.14)",
  text: "#0F1629", textSecondary: "#3D4A6B", textMuted: "#7A87A8",
  headerBg: "rgba(245,247,250,0.95)", scrollbarThumb: "rgba(10,29,68,0.12)",
  inputBg: "#FFFFFF",
};

/* ════════════════════════════════════════════════
   CSS
════════════════════════════════════════════════ */
const makeCSS = (C) => `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800;900&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; -webkit-font-smoothing:antialiased; }
  body { font-family:'DM Sans',sans-serif; background:${C.bg}; color:${C.text}; overflow-x:hidden; }
  a { color:inherit; text-decoration:none; }
  ::-webkit-scrollbar { width:5px; }
  ::-webkit-scrollbar-thumb { background:${C.scrollbarThumb}; border-radius:99px; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes pop     { 0%{transform:scale(0.85)} 60%{transform:scale(1.04)} 100%{transform:scale(1)} }
  @keyframes shake   { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-6px)} 75%{transform:translateX(6px)} }
  @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.55} }

  .fu  { animation:fadeUp 0.5s cubic-bezier(0.16,1,0.3,1) both; }
  .fu2 { animation:fadeUp 0.5s 0.07s cubic-bezier(0.16,1,0.3,1) both; }
  .fu3 { animation:fadeUp 0.5s 0.14s cubic-bezier(0.16,1,0.3,1) both; }
  .pop { animation:pop 0.4s cubic-bezier(0.16,1,0.3,1) both; }
  .shake { animation:shake 0.35s ease both; }

  .card { transition:box-shadow 0.2s ease,border-color 0.2s ease; }
  .card:hover { box-shadow:${C.shadowLg}; }

  .code-input {
    width:100%; font-family:'Sora',sans-serif; font-size:clamp(22px,5vw,36px);
    font-weight:900; letter-spacing:5px; text-align:center; text-transform:uppercase;
    background:${C.inputBg}; color:${C.text}; border:2px solid ${C.border};
    border-radius:16px; padding:18px 16px; outline:none;
    transition:border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .code-input:focus { border-color:${C.accent}; box-shadow:0 0 0 3px ${C.accentBg}; }
  .code-input::placeholder { color:${C.textMuted}; letter-spacing:2px; font-size:18px; }

  .btn-primary {
    display:flex; align-items:center; justify-content:center; gap:8px;
    width:100%; padding:14px 20px; border-radius:12px; border:none; cursor:pointer;
    background:${C.accentGrad}; color:#fff;
    font-family:'DM Sans',sans-serif; font-size:15px; font-weight:700;
    transition:opacity 0.15s ease, transform 0.15s ease;
  }
  .btn-primary:hover:not(:disabled) { opacity:0.92; transform:translateY(-1px); }
  .btn-primary:disabled { opacity:0.5; cursor:not-allowed; transform:none; }

  .btn-success {
    display:flex; align-items:center; justify-content:center; gap:8px;
    width:100%; padding:15px 20px; border-radius:12px; border:none; cursor:pointer;
    background:linear-gradient(135deg,#059669,#047857); color:#fff;
    font-family:'DM Sans',sans-serif; font-size:16px; font-weight:800;
    transition:opacity 0.15s ease, transform 0.15s ease;
    box-shadow:0 4px 20px rgba(5,150,105,0.35);
  }
  .btn-success:hover:not(:disabled) { opacity:0.92; transform:translateY(-1px); }
  .btn-success:disabled { opacity:0.5; cursor:not-allowed; transform:none; }

  .btn-ghost {
    display:flex; align-items:center; justify-content:center; gap:6px;
    padding:10px 18px; border-radius:10px; cursor:pointer;
    background:${C.bgCard}; color:${C.textSecondary};
    border:1.5px solid ${C.border}; font-family:'DM Sans',sans-serif;
    font-size:13px; font-weight:600; transition:all 0.15s ease;
  }
  .btn-ghost:hover { border-color:${C.borderMedium}; background:${C.bgAlt}; }

  .status-pill {
    display:inline-flex; align-items:center; gap:5px;
    padding:4px 10px; border-radius:99px; font-size:11px; font-weight:700;
  }

  .main-pad { max-width:900px; margin:0 auto; padding:36px 16px 100px; }
  @media(min-width:640px){ .main-pad { padding:48px 24px 100px; } }

  .two-col { display:grid; grid-template-columns:1fr; gap:16px; }
  @media(min-width:700px){ .two-col { grid-template-columns:1fr 1fr; } }

  .hist-row { display:grid; grid-template-columns:120px 1fr 100px 90px; gap:8px; align-items:center; }
  @media(max-width:580px){
    .hist-row { grid-template-columns:100px 1fr 80px; }
    .hist-hide-sm { display:none !important; }
  }

  .theme-toggle { transition:transform 0.2s ease; }
  .theme-toggle:hover { transform:rotate(15deg) scale(1.1); }

  .live-dot {
    width:7px; height:7px; border-radius:50%;
    background:${C.success};
    animation:pulse 1.6s ease-in-out infinite;
  }

  .taxi-avatar {
    width:64px; height:64px; border-radius:16px; object-fit:cover;
    border:2px solid ${C.border}; flex-shrink:0;
  }
`;

/* ════════════════════════════════════════════════
   HELPERS
════════════════════════════════════════════════ */
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("en-IE", { day:"numeric", month:"short", year:"numeric" }) : "—";

const fmtTime = (d) =>
  d ? new Date(d).toLocaleString("en-IE", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : "—";

const fmtSteps = (n) =>
  n >= 1_000_000 ? `${(n/1e6).toFixed(2)}M` : n >= 1000 ? `${(n/1000).toFixed(1)}K` : String(Math.round(n || 0));

const statusConfig = (status, expiresAt) => {
  const expired = expiresAt && new Date(expiresAt) < new Date();
  if (status === "used")    return { label:"Used",    dot:"#94A3B8", bg:"rgba(148,163,184,0.12)", text:"#64748B" };
  if (status === "revoked") return { label:"Revoked", dot:"#F87171", bg:"rgba(248,113,113,0.12)", text:"#DC2626" };
  if (status === "expired" || (status === "issued" && expired))
    return { label:"Expired", dot:"#F59E0B", bg:"rgba(245,158,11,0.12)", text:"#B45309" };
  return { label:"Active", dot:"#34D399", bg:"rgba(52,211,153,0.12)", text:"#059669" };
};

/* ════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════ */
export default function ProAmTaxiPortal() {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; }
  });
  const C = isDark ? DARK : LIGHT;
  const toggleTheme = useCallback(() => {
    setIsDark(v => { const n=!v; try{localStorage.setItem("spa-theme",n?"dark":"light");}catch{} return n; });
  },[]);

  // Auth + driver profile
  const [user, setUser]           = useState(null);
  const [taxiPartner, setTaxiPartner] = useState(null); // their taxi_partners row (if they are a driver)
  const [loadingAuth, setLoadingAuth] = useState(true);

  // Code entry
  const [codeInput, setCodeInput] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null); // { redemption, passenger }
  const [verifyError, setVerifyError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(null);
  const inputRef = useRef(null);
  const formRef  = useRef(null);

  // History
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);
  const [histPage, setHistPage] = useState(0);
  const PAGE_SIZE = 15;

  /* ── Auth ── */
  useEffect(() => {
    (async () => {
      const { data: { user: u } } = await supabase.auth.getUser();
      setUser(u);

      if (u) {
        // Check if this user is a registered taxi partner
        const { data: tp } = await supabase
          .from("taxi_partners")
          .select("*")
          .eq("profile_id", u.id)
          .eq("is_active", true)
          .single();
        setTaxiPartner(tp || null);
      }
      setLoadingAuth(false);
    })();
  }, []);

  /* ── Fetch history ── */
  const fetchHistory = useCallback(async () => {
    if (!user) return;
    setLoadingHist(true);
    const query = supabase
      .from("taxi_redemptions")
      .select(`
        id, code, status, issued_at, used_at, expires_at,
        steps_deducted, discount_value, taxi_partner_id,
        profiles:profile_id (name, surname, username)
      `)
      .order("issued_at", { ascending: false })
      .range(histPage * PAGE_SIZE, (histPage + 1) * PAGE_SIZE - 1);

    // Taxi drivers only see codes they used; admins (no taxi_partner row) see all
    if (taxiPartner) {
      query.eq("taxi_partner_id", taxiPartner.id);
    }

    const { data } = await query;
    setHistory(data || []);
    setLoadingHist(false);
  }, [user, taxiPartner, histPage]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  /* ── Verify code ── */
  const handleVerify = async (e) => {
    e?.preventDefault();
    const code = codeInput.trim().toUpperCase();
    if (code.length !== 10) {
      setVerifyError("Please enter a valid 10-character code.");
      if (formRef.current) formRef.current.classList.add("shake");
      setTimeout(() => formRef.current?.classList.remove("shake"), 400);
      return;
    }
    setVerifying(true);
    setVerifyError("");
    setVerifiedData(null);
    setConfirmSuccess(null);

    const { data, error } = await supabase
      .from("taxi_redemptions")
      .select(`
        id, code, status, issued_at, used_at, expires_at,
        steps_deducted, discount_value, taxi_partner_id,
        profiles:profile_id (name, surname, username, profile_img, steps)
      `)
      .eq("code", code)
      .single();

    setVerifying(false);

    if (error || !data) {
      setVerifyError("Code not found. Please check and try again.");
      formRef.current?.classList.add("shake");
      setTimeout(() => formRef.current?.classList.remove("shake"), 400);
      return;
    }

    // Expired check (client-side preview)
    const isExpired = data.expires_at && new Date(data.expires_at) < new Date();
    if (data.status !== "issued" || isExpired) {
      const reason =
        data.status === "used"    ? "This code has already been used." :
        data.status === "revoked" ? "This code has been revoked." :
        isExpired                 ? "This code has expired." :
        "This code is no longer valid.";
      setVerifyError(reason);
      formRef.current?.classList.add("shake");
      setTimeout(() => formRef.current?.classList.remove("shake"), 400);
      return;
    }

    setVerifiedData(data);
  };

  /* ── Confirm use ── */
  const handleConfirmUse = async () => {
    if (!verifiedData) return;
    setConfirming(true);

    const { data, error } = await supabase.rpc("use_taxi_code", {
      p_code:            verifiedData.code,
      p_taxi_partner_id: taxiPartner?.id || null,
    });

    setConfirming(false);

    if (error) {
      const msg =
        error.message?.includes("CODE_ALREADY_USED") ? "Code was just used by another driver — timing conflict." :
        error.message?.includes("CODE_EXPIRED")       ? "Code expired just now." :
        error.message?.includes("CODE_NOT_FOUND")     ? "Code not found in our system." :
        "Failed to mark code as used. Please try again.";
      setVerifyError(msg);
      setVerifiedData(null);
      return;
    }

    const result = typeof data === "string" ? JSON.parse(data) : data;
    setConfirmSuccess(result);
    setVerifiedData(null);
    setCodeInput("");
    await fetchHistory();
  };

  const handleReset = () => {
    setCodeInput("");
    setVerifiedData(null);
    setVerifyError("");
    setConfirmSuccess(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  /* ════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════ */
  if (loadingAuth) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",
      justifyContent:"center",flexDirection:"column",gap:16}}>
      <style>{makeCSS(C)}</style>
      <div style={{width:44,height:44,borderRadius:"50%",border:`3px solid ${C.border}`,
        borderTopColor:C.accent,animation:"spin 0.75s linear infinite"}}/>
      <div style={{fontSize:13,color:C.textMuted,fontWeight:600}}>Loading ProAm Taxi…</div>
    </div>
  );

  if (!user) return (
    <div style={{minHeight:"100vh",background:C.bg,color:C.text}}>
      <style>{makeCSS(C)}</style>
      <div style={{maxWidth:480,margin:"80px auto",padding:"0 24px",textAlign:"center"}}>
        <div style={{fontSize:56,marginBottom:16}}>🚕</div>
        <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:28,fontWeight:900,marginBottom:12}}>
          ProAm Taxi Portal
        </h1>
        <p style={{color:C.textSecondary,fontSize:15,lineHeight:1.65,marginBottom:24}}>
          This portal is for registered ProAm taxi drivers. Please log in with your ProAm
          account to verify discount codes.
        </p>
        <button className="btn-primary" onClick={() => navigate("/login")}
          style={{fontSize:15}}>
          Log In to Continue →
        </button>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:C.bg,color:C.text}}>
      <style>{makeCSS(C)}</style>

      {/* ══ HEADER ══ */}
      <header style={{position:"sticky",top:0,zIndex:100,height:56,padding:"0 16px",
        display:"flex",alignItems:"center",justifyContent:"space-between",
        background:C.headerBg,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",
        borderBottom:`1px solid ${C.border}`}}>

        <button onClick={()=>navigate("/")} style={{display:"flex",alignItems:"center",gap:8,
          background:"none",border:"none",cursor:"pointer",padding:0}}>
          <div style={{width:30,height:30,borderRadius:8,display:"flex",alignItems:"center",
            justifyContent:"center",background:C.bgElevated,border:`1px solid ${C.border}`}}>
            <img src={logo} alt="Snows ProAm" style={{width:18,height:18,objectFit:"contain"}}/>
          </div>
          <span style={{fontFamily:"'Sora',sans-serif",fontSize:14,fontWeight:700,color:C.text}}>
            ProAm <span style={{color:C.accent}}>Taxi</span>
          </span>
        </button>

        <div style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:999,
          background:C.successBg,border:`1px solid ${C.successBorder}`,
          fontSize:10,fontWeight:700,color:C.success,letterSpacing:0.4}}>
          <div className="live-dot"/>Driver Portal
        </div>

        <div style={{display:"flex",gap:8}}>
          <button className="theme-toggle" onClick={toggleTheme} style={{width:32,height:32,
            borderRadius:8,background:C.bgElevated,border:`1px solid ${C.border}`,
            color:C.text,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <FontAwesomeIcon icon={isDark?faSun:faMoon} style={{fontSize:12}}/>
          </button>
          <button onClick={()=>navigate(-1)} className="btn-ghost" style={{padding:"6px 12px",fontSize:12}}>
            ← Back
          </button>
        </div>
      </header>

      <div className="main-pad">

        {/* ══ DRIVER PROFILE BANNER ══ */}
        {taxiPartner && (
          <div className="fu" style={{background:C.bgCard,border:`1px solid ${C.border}`,
            borderRadius:20,padding:"20px 24px",marginBottom:28,
            boxShadow:C.shadowMd,display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
            {taxiPartner.driver_image_url && (
              <img src={taxiPartner.driver_image_url} alt={taxiPartner.driver_name}
                className="taxi-avatar"/>
            )}
            {!taxiPartner.driver_image_url && (
              <div style={{width:64,height:64,borderRadius:16,background:C.accentBg,
                border:`2px solid ${C.accentBorder}`,display:"flex",alignItems:"center",
                justifyContent:"center",fontSize:28,flexShrink:0}}>🚕</div>
            )}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,fontWeight:700,color:C.textMuted,
                textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Verified Driver</div>
              <div style={{fontFamily:"'Sora',sans-serif",fontSize:20,fontWeight:800,color:C.text,
                marginBottom:4}}>{taxiPartner.driver_name || taxiPartner.name}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {[
                  taxiPartner.car_type && { icon:"🚗", val:taxiPartner.car_type },
                  taxiPartner.car_reg  && { icon:"🔢", val:taxiPartner.car_reg  },
                  taxiPartner.badge_number && { icon:"🪪", val:`Badge: ${taxiPartner.badge_number}` },
                  taxiPartner.service_area && { icon:"📍", val:taxiPartner.service_area },
                ].filter(Boolean).map((item,i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:5,fontSize:12,
                    fontWeight:600,color:C.textSecondary,padding:"3px 8px",borderRadius:6,
                    background:C.bgAlt,border:`1px solid ${C.border}`}}>
                    {item.icon} {item.val}
                  </div>
                ))}
              </div>
            </div>
            {taxiPartner.car_image_url && (
              <img src={taxiPartner.car_image_url} alt="Car"
                style={{width:120,height:80,borderRadius:12,objectFit:"cover",
                  border:`1px solid ${C.border}`,flexShrink:0}}/>
            )}
          </div>
        )}

        {!taxiPartner && (
          <div className="fu" style={{background:C.amberBg,border:`1px solid ${C.amberBorder}`,
            borderRadius:16,padding:"14px 18px",marginBottom:24,
            display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:18,flexShrink:0}}>⚠️</span>
            <div>
              <div style={{fontWeight:700,color:C.amber,marginBottom:3,fontSize:13}}>
                Not a registered driver
              </div>
              <div style={{fontSize:12,color:C.textSecondary,lineHeight:1.5}}>
                Your account is not linked to a ProAm taxi partner. You can still look up
                codes below, but only registered drivers can mark codes as used.
                Contact <strong>support@snowsproam.ie</strong> to get registered.
              </div>
            </div>
          </div>
        )}

        {/* ══ CODE ENTRY ══ */}
        <div className="fu2" style={{marginBottom:28}}>
          <div style={{marginBottom:16}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:7,padding:"4px 12px",
              borderRadius:999,background:C.accentBg,border:`1px solid ${C.accentBorder}`,
              fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",
              letterSpacing:0.5,marginBottom:10}}>
              <span style={{width:5,height:5,borderRadius:"50%",background:C.accent}}/>
              Verify Discount Code
            </div>
            <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(20px,4vw,26px)",
              fontWeight:800,color:C.text,letterSpacing:-0.4,marginBottom:6}}>
              Enter passenger code
            </h2>
            <p style={{fontSize:13,color:C.textSecondary,lineHeight:1.65}}>
              Ask the passenger for their 10-character ProAm Taxi discount code.
              Verify it here before applying the €5.00 discount to their fare.
            </p>
          </div>

          <div ref={formRef} style={{background:C.bgCard,border:`1px solid ${C.border}`,
            borderRadius:20,padding:"24px",boxShadow:C.shadowMd}}>

            {/* Input form */}
            {!verifiedData && !confirmSuccess && (
              <form onSubmit={handleVerify}>
                <input
                  ref={inputRef}
                  className="code-input"
                  value={codeInput}
                  onChange={e => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,10);
                    setCodeInput(v);
                    setVerifyError("");
                  }}
                  placeholder="XXXXXXXXXX"
                  maxLength={10}
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />

                {/* Character count indicator */}
                <div style={{display:"flex",gap:6,justifyContent:"center",
                  margin:"12px 0 16px",flexWrap:"wrap"}}>
                  {Array.from({length:10}).map((_,i) => (
                    <div key={i} style={{
                      width:22,height:6,borderRadius:99,
                      background:i < codeInput.length ? C.accent : C.border,
                      transition:"background 0.15s ease",
                    }}/>
                  ))}
                </div>

                {verifyError && (
                  <div style={{background:C.dangerBg,border:`1px solid ${C.dangerBorder}`,
                    borderRadius:10,padding:"10px 14px",marginBottom:14,
                    display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{fontSize:16,flexShrink:0}}>❌</span>
                    <span style={{fontSize:13,fontWeight:600,color:C.danger}}>{verifyError}</span>
                  </div>
                )}

                <button className="btn-primary" type="submit"
                  disabled={verifying || codeInput.length !== 10}
                  style={{fontSize:15}}>
                  {verifying
                    ? <><div style={{width:18,height:18,borderRadius:"50%",border:`2px solid rgba(255,255,255,0.3)`,borderTopColor:"#fff",animation:"spin 0.7s linear infinite"}}/>Verifying…</>
                    : <>🔍 Verify Code</>
                  }
                </button>
              </form>
            )}

            {/* Verification result */}
            {verifiedData && !confirmSuccess && (() => {
              const p = verifiedData.profiles || {};
              const displayName = [p.name, p.surname].filter(Boolean).join(" ") || p.username || "Unknown passenger";
              return (
                <div className="pop">
                  {/* Code banner */}
                  <div style={{background:C.successBg,border:`2px solid ${C.successBorder}`,
                    borderRadius:14,padding:"16px 20px",marginBottom:20,textAlign:"center"}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.success,
                      textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>
                      ✅ Valid Code Found
                    </div>
                    <div style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(24px,6vw,40px)",
                      fontWeight:900,color:C.text,letterSpacing:5,marginBottom:4}}>
                      {verifiedData.code}
                    </div>
                    <div style={{fontSize:13,fontWeight:600,color:C.success}}>
                      €{parseFloat(verifiedData.discount_value).toFixed(2)} discount — Ready to use
                    </div>
                  </div>

                  {/* Passenger card */}
                  <div style={{background:C.bgAlt,borderRadius:12,padding:"16px 18px",
                    marginBottom:16,display:"flex",gap:12,alignItems:"center"}}>
                    {p.profile_img
                      ? <img src={p.profile_img} alt={displayName}
                          style={{width:48,height:48,borderRadius:"50%",objectFit:"cover",
                            border:`1.5px solid ${C.border}`,flexShrink:0}}/>
                      : <div style={{width:48,height:48,borderRadius:"50%",display:"flex",
                          alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,
                          background:C.accentBg,border:`1.5px solid ${C.accentBorder}`,
                          color:C.accent,flexShrink:0}}>
                          {(displayName[0]||"?").toUpperCase()}
                        </div>
                    }
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:15,color:C.text,marginBottom:3}}>
                        {displayName}
                      </div>
                      {p.username && (
                        <div style={{fontSize:12,color:C.textMuted}}>@{p.username}</div>
                      )}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:11,fontWeight:700,color:C.textMuted,
                        textTransform:"uppercase",letterSpacing:0.4,marginBottom:2}}>Total steps</div>
                      <div style={{fontFamily:"'Sora',sans-serif",fontSize:16,fontWeight:800,
                        color:C.accent}}>{fmtSteps(p.steps)}</div>
                    </div>
                  </div>

                  {/* Code details grid */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
                    {[
                      { label:"Discount value",  val:`€${parseFloat(verifiedData.discount_value).toFixed(2)}`, color:C.success },
                      { label:"Steps spent",     val:fmtSteps(verifiedData.steps_deducted),                  color:C.accent  },
                      { label:"Code issued",     val:fmt(verifiedData.issued_at),                             color:C.text    },
                      { label:"Code expires",    val:fmt(verifiedData.expires_at),                            color:C.text    },
                    ].map((row,i) => (
                      <div key={i} style={{background:C.bgCard,border:`1px solid ${C.border}`,
                        borderRadius:10,padding:"12px 14px"}}>
                        <div style={{fontSize:10,fontWeight:700,color:C.textMuted,
                          textTransform:"uppercase",letterSpacing:0.4,marginBottom:4}}>
                          {row.label}
                        </div>
                        <div style={{fontFamily:"'Sora',sans-serif",fontSize:15,
                          fontWeight:800,color:row.color}}>
                          {row.val}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{display:"flex",gap:10}}>
                    <button className="btn-ghost" onClick={handleReset}
                      style={{flex:"0 0 auto",padding:"13px 18px"}}>
                      ← Back
                    </button>
                    <button
                      className="btn-success"
                      onClick={handleConfirmUse}
                      disabled={confirming || !taxiPartner}
                      style={{flex:1}}
                    >
                      {confirming
                        ? <><div style={{width:18,height:18,borderRadius:"50%",border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",animation:"spin 0.7s linear infinite"}}/>Confirming…</>
                        : <>✅ Apply €{parseFloat(verifiedData.discount_value).toFixed(2)} Discount</>
                      }
                    </button>
                  </div>

                  {!taxiPartner && (
                    <div style={{fontSize:11,color:C.amber,textAlign:"center",marginTop:8,
                      fontWeight:600}}>
                      ⚠️ You must be a registered driver to mark codes as used.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Success confirmation */}
            {confirmSuccess && (() => {
              const red  = confirmSuccess.redemption || confirmSuccess;
              const pass = confirmSuccess.passenger || {};
              const displayName = [pass.name, pass.surname].filter(Boolean).join(" ") || pass.username || "Passenger";
              return (
                <div className="pop" style={{textAlign:"center"}}>
                  <div style={{fontSize:60,marginBottom:12}}>🎉</div>
                  <div style={{fontFamily:"'Sora',sans-serif",fontSize:24,fontWeight:900,
                    color:C.text,marginBottom:8}}>Discount Applied!</div>
                  <div style={{fontSize:14,color:C.textSecondary,marginBottom:20,lineHeight:1.6}}>
                    €{parseFloat(red.discount_value || 5).toFixed(2)} off successfully
                    applied for <strong>{displayName}</strong>.
                    The code has been marked as used and logged.
                  </div>
                  <div style={{background:C.successBg,border:`1.5px solid ${C.successBorder}`,
                    borderRadius:14,padding:"16px 20px",marginBottom:20}}>
                    <div style={{fontFamily:"'Sora',sans-serif",fontSize:32,fontWeight:900,
                      color:C.success,letterSpacing:4,marginBottom:4}}>
                      {red.code}
                    </div>
                    <div style={{fontSize:12,fontWeight:600,color:C.success}}>
                      ✓ Marked as used · {fmtTime(red.used_at || new Date().toISOString())}
                    </div>
                  </div>
                  <button className="btn-primary" onClick={handleReset} style={{fontSize:15}}>
                    Verify Another Code →
                  </button>
                </div>
              );
            })()}
          </div>
        </div>

        {/* ══ REDEMPTION HISTORY ══ */}
        <div className="fu3">
          <div style={{marginBottom:16}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:7,padding:"4px 12px",
              borderRadius:999,background:C.accentBg,border:`1px solid ${C.accentBorder}`,
              fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",
              letterSpacing:0.5,marginBottom:10}}>
              <span style={{width:5,height:5,borderRadius:"50%",background:C.accent}}/>
              {taxiPartner ? "Your Redemptions" : "All Redemptions"}
            </div>
            <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(18px,3vw,22px)",
              fontWeight:800,color:C.text,letterSpacing:-0.4,marginBottom:6}}>
              {taxiPartner
                ? "Codes you've processed"
                : "Full redemption ledger"}
            </h2>
            <p style={{fontSize:13,color:C.textSecondary}}>
              {taxiPartner
                ? "A record of all discount codes you've verified and applied."
                : "All issued, used, and expired ProAm taxi discount codes across all drivers."}
            </p>
          </div>

          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,
            borderRadius:20,overflow:"hidden",boxShadow:C.shadowMd}}>

            {/* Table header */}
            <div className="hist-row" style={{padding:"11px 16px",background:C.bgAlt,
              borderBottom:`1px solid ${C.border}`,fontSize:10,fontWeight:700,
              color:C.textMuted,textTransform:"uppercase",letterSpacing:0.4}}>
              <div>Code</div>
              <div>Passenger</div>
              <div style={{textAlign:"right"}}>Status</div>
              <div className="hist-hide-sm" style={{textAlign:"right"}}>Date</div>
            </div>

            {loadingHist ? (
              <div style={{padding:"40px",textAlign:"center"}}>
                <div style={{width:36,height:36,borderRadius:"50%",border:`3px solid ${C.border}`,
                  borderTopColor:C.accent,animation:"spin 0.75s linear infinite",margin:"0 auto"}}/>
              </div>
            ) : history.length === 0 ? (
              <div style={{padding:"48px",textAlign:"center"}}>
                <div style={{fontSize:40,marginBottom:12}}>🎟</div>
                <div style={{fontSize:13,fontWeight:600,color:C.textMuted}}>
                  {taxiPartner ? "No codes processed yet." : "No redemptions yet."}
                </div>
              </div>
            ) : history.map((row, i) => {
              const status = statusConfig(row.status, row.expires_at);
              const p = row.profiles || {};
              const displayName = [p.name,p.surname].filter(Boolean).join(" ") || p.username || "—";
              return (
                <div key={row.id} className="hist-row" style={{
                  padding:"12px 16px",
                  borderBottom: i < history.length - 1 ? `1px solid ${C.border}` : "none",
                  background: row.status === "used" ? `${C.successBg}` : "transparent",
                  transition:"background 0.15s ease",
                }}>
                  <div style={{fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:800,
                    color:C.text,letterSpacing:1}}>{row.code}</div>
                  <div style={{fontSize:12,fontWeight:600,color:C.textSecondary,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {displayName}
                    <div style={{fontSize:10,color:C.textMuted,marginTop:1,fontWeight:400}}>
                      {row.status === "used" && row.used_at ? `Used: ${fmtTime(row.used_at)}` : `Issued: ${fmtTime(row.issued_at)}`}
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <span className="status-pill" style={{background:status.bg,color:status.text}}>
                      <span style={{width:5,height:5,borderRadius:"50%",background:status.dot,flexShrink:0}}/>
                      {status.label}
                    </span>
                  </div>
                  <div className="hist-hide-sm" style={{textAlign:"right",fontSize:11,
                    fontWeight:600,color:C.textMuted}}>
                    {fmt(row.used_at || row.issued_at)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:14}}>
            <button className="btn-ghost" disabled={histPage === 0}
              onClick={() => setHistPage(p => Math.max(0, p-1))}>
              ← Previous
            </button>
            <div style={{display:"flex",alignItems:"center",fontSize:13,color:C.textMuted,
              fontWeight:600,padding:"0 8px"}}>
              Page {histPage + 1}
            </div>
            <button className="btn-ghost" disabled={history.length < PAGE_SIZE}
              onClick={() => setHistPage(p => p+1)}>
              Next →
            </button>
          </div>
        </div>

        {/* ══ INFO CARDS ══ */}
        <div className="two-col" style={{marginTop:28}}>
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:16,
            padding:"20px",boxShadow:C.shadowMd}}>
            <div style={{fontSize:20,marginBottom:10}}>📋</div>
            <div style={{fontFamily:"'Sora',sans-serif",fontSize:15,fontWeight:800,
              color:C.text,marginBottom:8}}>How to apply a discount</div>
            {[
              "Ask the passenger to open their ProAm app and go to ProAm Taxi.",
              "The passenger reads out their 10-character code.",
              "Enter it above and tap Verify Code.",
              "Confirm the passenger's name matches.",
              'Tap "Apply Discount" to deduct €5.00 from the fare.',
              "The code is instantly invalidated — no double-use possible.",
            ].map((step, i) => (
              <div key={i} style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:C.accentBg,
                  border:`1px solid ${C.accentBorder}`,display:"flex",alignItems:"center",
                  justifyContent:"center",fontSize:10,fontWeight:800,color:C.accent,flexShrink:0}}>
                  {i+1}
                </div>
                <div style={{fontSize:12,color:C.textSecondary,lineHeight:1.55,paddingTop:2}}>
                  {step}
                </div>
              </div>
            ))}
          </div>

          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:16,
            padding:"20px",boxShadow:C.shadowMd}}>
            <div style={{fontSize:20,marginBottom:10}}>🛡️</div>
            <div style={{fontFamily:"'Sora',sans-serif",fontSize:15,fontWeight:800,
              color:C.text,marginBottom:8}}>Fraud prevention</div>
            {[
              {title:"One-time use", desc:"Each code is cryptographically unique and becomes invalid the moment it's confirmed."},
              {title:"Expiry enforced", desc:"Codes expire 30 days after generation, server-side — clients cannot bypass this."},
              {title:"Step snapshot", desc:"The passenger's step count is recorded at redemption time, preventing retroactive manipulation."},
              {title:"Full audit trail", desc:"Every action (issued, viewed, used, expired) is logged with timestamp and actor ID."},
              {title:"No offline bypass", desc:"Confirmation requires a live Supabase RPC call. No offline or cached approvals."},
            ].map((item,i) => (
              <div key={i} style={{marginBottom:12,paddingBottom:12,
                borderBottom:i<4?`1px solid ${C.border}`:"none"}}>
                <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:2}}>
                  🔒 {item.title}
                </div>
                <div style={{fontSize:12,color:C.textMuted,lineHeight:1.55}}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ FOOTER ══ */}
        <div style={{marginTop:40,textAlign:"center",fontSize:12,color:C.textMuted,lineHeight:1.6}}>
          ProAm Taxi Portal · Powered by{" "}
          <span style={{color:C.accent,fontWeight:700}}>Snows ProAm</span>
          {" "}· For driver support contact{" "}
          <a href="mailto:support@snowsproam.ie"
            style={{color:C.accent,fontWeight:600}}>support@snowsproam.ie</a>
        </div>

      </div>
    </div>
  );
}